/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import { parseMarkdown } from '../utils/markdown';
import { ServiceRegistry } from '../utils/ServiceRegistry';
import { StatusManager } from '../utils/StatusManager';
import { ErrorHandler } from '../utils/ErrorHandler';
import { StatusMessages } from '../utils/constants';
import type { DomController } from './DomController';
import type { GeminiService } from './GeminiService';
import type { NoteService } from './NoteService';
import type { SettingsService } from './SettingsService';
import type { EditorService } from './EditorService';
import type { EditorActionService } from './EditorActionService';
import type { UIService } from './UIService';

/**
 * Manages all aspects of audio recording, processing, and visualization.
 */
export class AudioService {
  private statusManager: StatusManager;
  
  // Service registry getters
  private get dom() { return ServiceRegistry.get<DomController>('dom'); }
  private get gemini() { return ServiceRegistry.get<GeminiService>('gemini'); }
  private get noteService() { return ServiceRegistry.get<NoteService>('noteService'); }
  private get settingsService() { return ServiceRegistry.get<SettingsService>('settingsService'); }
  private get uiService() { return ServiceRegistry.get<UIService>('uiService'); }
  private get editorService() { return ServiceRegistry.get<EditorService>('editorService'); }
  private get editorActionService() { return ServiceRegistry.get<EditorActionService>('editorActionService'); }

  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private isRecording = false;
  private stream: MediaStream | null = null;
  private isPushToTalkRecording = false;
  private isRecordingCancelled = false;
  private audioContext: AudioContext | null = null;
  private analyserNode: AnalyserNode | null = null;
  private waveformDataArray: Uint8Array | null = null;
  private waveformDrawingId: number | null = null;
  private timerIntervalId: number | null = null;
  private recordingStartTime = 0;

  private commandMediaRecorder: MediaRecorder | null = null;
  private isCommandRecording = false;

  constructor() {
    this.statusManager = new StatusManager(this.dom);
  }
  
  public getIsRecording = (): boolean => this.isRecording;
  public getIsPushToTalkRecording = (): boolean => this.isPushToTalkRecording;
  public setPushToTalkRecording = (value: boolean): void => { this.isPushToTalkRecording = value; };

  public async toggleRecording(): Promise<void> {
    if (!this.isRecording) await this.startRecording(true);
    else await this.stopRecording();
  }

  public setupAudioVisualizer(): void {
    if (!this.stream || this.audioContext) return;
    this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const source = this.audioContext.createMediaStreamSource(this.stream);
    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = 256;
    this.analyserNode.smoothingTimeConstant = 0.75;
    this.waveformDataArray = new Uint8Array(this.analyserNode.frequencyBinCount);
    source.connect(this.analyserNode);
  }

  public startLiveUpdates(): void {
    this.dom.startLiveDisplay(this.noteService.currentNote?.title || '');
    this.setupAudioVisualizer();
    this.waveformDrawingId = this.dom.drawLiveWaveform(this.analyserNode, this.waveformDataArray, this.isRecording);
    this.recordingStartTime = Date.now();
    this.dom.updateLiveTimer(this.isRecording, this.recordingStartTime);
    if (this.timerIntervalId) clearInterval(this.timerIntervalId);
    this.timerIntervalId = window.setInterval(() => this.dom.updateLiveTimer(this.isRecording, this.recordingStartTime), 50);
  }
  
  public stopLiveUpdates(): void {
    this.dom.stopLiveDisplay();
    if (this.waveformDrawingId) cancelAnimationFrame(this.waveformDrawingId);
    this.waveformDrawingId = null;
    if (this.timerIntervalId) clearInterval(this.timerIntervalId);
    this.timerIntervalId = null;
    if (this.audioContext) {
      if (this.audioContext.state !== 'closed') this.audioContext.close().catch(e => console.warn('Error closing audio context', e));
      this.audioContext = null;
    }
    this.analyserNode = null;
    this.waveformDataArray = null;
  }

  public async startRecordingAtCursor(): Promise<void> {
    this.editorService.storeSelection();
    await this.startRecording(false); // Don't re-store selection
  }

  public async startRecording(shouldStoreSelection: boolean): Promise<void> {
    try {
      if (shouldStoreSelection) this.editorService.storeSelection();

      this.audioChunks = [];
      if (this.stream) this.stream.getTracks().forEach(track => track.stop());
      if (this.audioContext && this.audioContext.state !== 'closed') await this.audioContext.close();
      this.audioContext = null;
      this.statusManager.loading('Requesting microphone access');

      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType: 'audio/webm' });
      this.mediaRecorder.ondataavailable = event => { if (event.data.size > 0) this.audioChunks.push(event.data); };
      this.mediaRecorder.onstop = () => {
        this.stopLiveUpdates();
        
        // Reset cancellation flag for next recording
        const wasCancelled = this.isRecordingCancelled;
        this.isRecordingCancelled = false;
        
        if (this.audioChunks.length > 0 && !wasCancelled) {
          const audioBlob = new Blob(this.audioChunks, { type: this.mediaRecorder?.mimeType || 'audio/webm' });
          
          // Save as an audio clip first
          if (this.noteService.currentNote) {
              const now = new Date();
              const clip = {
                  id: `clip_${now.getTime()}`,
                  name: `Recording - ${now.toLocaleString()}`,
                  data: audioBlob,
                  mimeType: this.mediaRecorder?.mimeType || 'audio/webm',
                  driveFileId: null
              };
              if (!this.noteService.currentNote.audioClips) {
                  this.noteService.currentNote.audioClips = [];
              }
              this.noteService.currentNote.audioClips.push(clip);
              this.noteService.renderNoteUI(); // Re-render UI to show new clip player
              this.noteService.saveCurrentNote(false); // Save note with new clip but keep dirty
          }

          // Check recording mode before processing
          if (this.settingsService.recordingMode === 'transcription') {
            this.processAudio(audioBlob).catch(err => {
              ErrorHandler.handle('processing audio', err, this.dom);
            });
          } else {
            this.statusManager.success('Voice note saved');
          }
        } else if (wasCancelled) {
          // Recording was cancelled, do nothing
          this.statusManager.custom('Recording cancelled');
        } else {
          this.statusManager.error('No audio data captured. Please try again.');
        }
        if (this.stream) this.stream.getTracks().forEach(track => track.stop());
        this.stream = null;
      };

      this.mediaRecorder.start();
      this.isRecording = true;
      this.dom.recordButton.classList.add('recording');
      this.dom.recordButton.setAttribute('title', 'Stop Recording');
      this.dom.cancelRecordingButton.classList.remove('hidden');
      this.startLiveUpdates();
    } catch (error) {
      ErrorHandler.handle('starting recording', error, this.dom, false);
      let errorMessage = 'An error occurred while starting the recording.';
      if (error instanceof Error) {
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') errorMessage = 'Microphone permission denied.';
          else if (error.name === 'NotFoundError') errorMessage = 'No microphone found.';
          else if (error.name === 'NotReadableError') errorMessage = 'Cannot access microphone. It may be in use.';
      }
      this.statusManager.custom(errorMessage);
      this.isRecording = false;
      if (this.stream) this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
      this.dom.recordButton.classList.remove('recording');
      this.dom.recordButton.setAttribute('title', 'Start/Stop Recording (or hold Space)');
      this.stopLiveUpdates();
    }
  }

  public async stopRecording(): Promise<void> {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.isPushToTalkRecording = false;
      this.dom.recordButton.classList.remove('recording');
      this.dom.recordButton.setAttribute('title', 'Start/Stop Recording (or hold Space)');
      this.statusManager.loading('Processing audio');
      
      // Hide cancel button when recording stops normally
      this.dom.cancelRecordingButton.classList.add('hidden');
    }
  }

  public async cancelRecording(): Promise<void> {
    if (this.mediaRecorder && this.isRecording) {
      // Set cancellation flag before stopping
      this.isRecordingCancelled = true;
      
      this.mediaRecorder.stop();
      this.isRecording = false;
      this.isPushToTalkRecording = false;
      this.dom.recordButton.classList.remove('recording');
      this.dom.recordButton.setAttribute('title', 'Start/Stop Recording (or hold Space)');
      this.dom.updateStatus('Recording cancelled.');
      
      // Clear audio chunks to discard the recording
      this.audioChunks = [];
      
      // Hide cancel button
      this.dom.cancelRecordingButton.classList.add('hidden');
    }
  }

  private async processAudio(audioBlob: Blob): Promise<void> {
    if (audioBlob.size === 0) {
        this.statusManager.error('No audio data captured. Please try again.');
        return;
    }
    const isAppendMode = this.noteService.currentNote && (this.noteService.currentNote.rawTranscription.trim() !== '' || this.noteService.currentNote.polishedNote.trim() !== '');
    const activeEditor = this.editorService.getActiveEditor();
    
    try {
        this.statusManager.loading('Converting audio');
        const reader = new FileReader();
        const base64Audio = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(audioBlob);
        });
        
        this.statusManager.loading('Getting transcription');
        const transcriptionModel = this.settingsService.getVoiceTranscriptionModel();
        const fastModel = this.settingsService.getFastModel();
        const rewriteModel = this.settingsService.getRewritePolishingModel();
        const noteId = this.noteService.currentNote?.id ?? null;
        const { text: transcriptionText, usageMetadata: transcriptionMeta } = await this.gemini.getTranscription(
          base64Audio,
          this.mediaRecorder?.mimeType || 'audio/webm',
          transcriptionModel,
        );
        this.uiService.addTokenUsageRecord('Transcription', transcriptionMeta, transcriptionModel, noteId);
        
        if (!transcriptionText) {
            this.statusManager.error('Transcription failed or returned empty');
            return;
        }

        // Check if transcription contains meaningful spoken content
        const meaningfulText = this.extractMeaningfulContent(transcriptionText);
        if (!meaningfulText) {
            this.statusManager.warning('No speech detected in recording. Please try again with clear speech.');
            return;
        }

        // Use meaningful text for further processing
        const processText = meaningfulText;

        if (isAppendMode) {
            if (activeEditor?.id === 'rawTranscription') {
                this.editorService.insertContent(processText);
            } else {
                if(this.noteService.currentNote) this.noteService.currentNote.rawTranscription += `\n\n${processText}`;
            }
        } else {
            if (this.noteService.currentNote) this.noteService.currentNote.rawTranscription = processText;
        }

        // Check if we should detect language or use output language setting
        let targetLanguage: string;
        let statusMessage: string;
        
        if (this.settingsService.outputLanguage === 'autodetect') {
            this.statusManager.progress('Transcription', 'Detecting language');
            const { text: detectedLanguage } = await this.gemini.detectLanguage(processText, fastModel);
            targetLanguage = detectedLanguage;
            statusMessage = `Language: ${detectedLanguage}`;
        } else {
            targetLanguage = this.settingsService.outputLanguage;
            statusMessage = `Target: ${targetLanguage}`;
        }
        
        if (this.settingsService.isPolishingEnabled) {
            this.statusManager.progress('Polishing', statusMessage);
            const { text: polishedText, usageMetadata: polishMeta } = await this.gemini.getPolishedNote(processText, targetLanguage, rewriteModel);
            this.uiService.addTokenUsageRecord('Polish Recording', polishMeta, rewriteModel, noteId);

            if (isAppendMode) {
                if (activeEditor?.id === 'polishedNote') {
                    this.editorService.insertContent(await parseMarkdown(polishedText));
                } else if (activeEditor?.id === 'translatedNote') {
                    if (this.noteService.currentNote) this.noteService.currentNote.polishedNote += `\n\n${polishedText}`;
                } else {
                    if(this.noteService.currentNote) this.noteService.currentNote.polishedNote += `\n\n${polishedText}`;
                }
            } else {
                if (this.noteService.currentNote) this.noteService.currentNote.polishedNote = polishedText;
                this.noteService.updateTitleFromContent(polishedText);
            }
        } else if (!isAppendMode) {
            if (this.noteService.currentNote) this.noteService.currentNote.polishedNote = '';
        }
        
        this.noteService.setDirty(true);

        if (this.settingsService.isTranslationEnabled) {
            await this.editorActionService.translateCurrentNote(targetLanguage, isAppendMode);
            this.statusManager.progress('Translating', 'Translation complete');
        } else {
            this.statusManager.success('Processing complete');
        }
        
        await this.noteService.renderNoteUI();
        await this.noteService.saveCurrentNote();

    } catch (error) {
        ErrorHandler.handle('processing recording', error, this.dom);
    }
  }

  public async toggleCommandRecording(): Promise<void> {
    if (this.isCommandRecording) {
        this.stopCommandRecording();
    } else if (!this.isRecording) { // Prevent starting command recording if main recording is active
        await this.startCommandRecording();
    } else {
        this.statusManager.error('Cannot dictate a command while recording a note.');
    }
  }

  private async startCommandRecording(): Promise<void> {
    try {
        this.audioChunks = []; // Reuse audioChunks for simplicity
        this.statusManager.loading('Listening for command');
        this.dom.recordButton.disabled = true;

        this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        this.commandMediaRecorder = new MediaRecorder(this.stream, { mimeType: 'audio/webm' });
        
        this.commandMediaRecorder.ondataavailable = event => { 
            if (event.data.size > 0) this.audioChunks.push(event.data); 
        };
        
        this.commandMediaRecorder.onstop = () => {
            if (this.audioChunks.length > 0) {
                const audioBlob = new Blob(this.audioChunks, { type: this.commandMediaRecorder?.mimeType || 'audio/webm' });
                this.processCommandAudio(audioBlob).catch(err => {
                    ErrorHandler.handle('processing command audio', err, this.dom);
                });
            } else {
                this.statusManager.custom('No command audio captured. Ready');
            }
            if (this.stream) this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        };

        this.commandMediaRecorder.start();
        this.isCommandRecording = true;
        this.dom.actionVoiceButton.classList.add('recording');
        const icon = this.dom.actionVoiceButton.querySelector('i');
        if (icon) icon.className = 'fas fa-stop';

    } catch (error) {
        ErrorHandler.handle('starting command recording', error, this.dom, false);
        let errorMessage = 'Microphone access denied or unavailable.';
        if (error instanceof Error) {
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') errorMessage = 'Microphone permission denied.';
        }
        this.statusManager.custom(errorMessage);
        this.isCommandRecording = false;
        this.dom.actionVoiceButton.classList.remove('recording');
        const icon = this.dom.actionVoiceButton.querySelector('i');
        if (icon) icon.className = 'fas fa-microphone';
        this.dom.recordButton.disabled = false;
    }
  }

  private stopCommandRecording(): void {
    if (this.commandMediaRecorder && this.isCommandRecording) {
        this.commandMediaRecorder.stop();
        this.isCommandRecording = false;
        this.dom.actionVoiceButton.classList.remove('recording');
        const icon = this.dom.actionVoiceButton.querySelector('i');
        if (icon) icon.className = 'fas fa-microphone';
        this.dom.recordButton.disabled = false;
        this.statusManager.loading('Processing command');
    }
  }

  private async processCommandAudio(audioBlob: Blob): Promise<void> {
    if (audioBlob.size === 0) {
        this.statusManager.ready();
        return;
    }
    try {
        const reader = new FileReader();
        const base64Audio = await new Promise<string>((resolve, reject) => {
            reader.onloadend = () => resolve((reader.result as string).split(',')[1]);
            reader.onerror = () => reject(reader.error);
            reader.readAsDataURL(audioBlob);
        });
        
        const transcriptionModel = this.settingsService.getVoiceTranscriptionModel();
        const { text: transcriptionText, usageMetadata } = await this.gemini.getTranscription(
          base64Audio,
          this.commandMediaRecorder?.mimeType || 'audio/webm',
          transcriptionModel,
        );
        this.uiService.addTokenUsageRecord('Dictate Command', usageMetadata, transcriptionModel, null);

        // Check if command transcription contains meaningful spoken content
        const meaningfulCommand = this.extractMeaningfulContent(transcriptionText);
        if (!meaningfulCommand) {
            this.dom.updateStatus('No speech detected in command. Please try again with clear speech.');
            return;
        }

        if (meaningfulCommand) {
            this.dom.actionInput.value = meaningfulCommand;
            this.dom.actionInput.focus();
            this.dom.updateStatus('Command transcribed. Press Execute to run.');
        } else {
            this.dom.updateStatus('Could not understand command. Ready.');
        }

    } catch (error) {
        console.error('Error in processCommandAudio:', error);
        this.dom.updateStatus('Error processing command. Please try again.');
    }
  }

  /**
   * Extracts meaningful spoken content from transcription text.
   * Filters out silence, filler words, and non-speech artifacts.
   */
  private extractMeaningfulContent(transcriptionText: string): string {
    if (!transcriptionText || typeof transcriptionText !== 'string') {
      return '';
    }

    // Trim whitespace and check for empty content
    const trimmed = transcriptionText.trim();
    if (!trimmed) {
      return '';
    }

    // Check for common non-speech indicators
    const nonSpeechPatterns = [
      /^\s*[\.\-_]*\s*$/, // Only dots, dashes, or underscores
      /^\s*\[.*?\]\s*$/, // Bracketed content like [music], [silence]
      /^\s*(mm+hm+|uh+huh+|yeah|yes|no|ok|okay|alright)\s*$/i, // Simple affirmations
      /^\s*(ah|um|er|uh|hm+)\s*$/i, // Filler words only
      /^\s*[^\w\s]*\s*$/, // Only non-word characters
    ];

    // Check if content matches any non-speech pattern
    for (const pattern of nonSpeechPatterns) {
      if (pattern.test(trimmed)) {
        return '';
      }
    }

    // Remove common filler words and clean up the text
    const cleanedText = trimmed
      .replace(/\b(um|uh|er|ah|hm|mm|mhm|uh+huh|like|you know|kind of|sort of|actually|basically)\b/gi, '') // Remove filler words
      .replace(/\s+/g, ' ') // Normalize whitespace
      .replace(/^\s+|\s+$/g, ''); // Trim again

    // Check if we have meaningful content after cleaning
    const wordCount = cleanedText.split(/\s+/).filter(word => word.length > 0).length;
    const hasAlphaChars = /[a-zA-Z]/.test(cleanedText);

    // Require at least 2 meaningful words or 1 word with alpha characters
    if (wordCount < 2 && !hasAlphaChars) {
      return '';
    }

    if (wordCount < 2 && hasAlphaChars) {
      // Single word - check if it's meaningful (not just a letter or number)
      const meaningfulWord = cleanedText.length > 2 && !/^[0-9a-zA-Z]$/.test(cleanedText);
      return meaningfulWord ? cleanedText : '';
    }

    return cleanedText;
  }
}